/**
 * Copyright (c) 2019 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { OrderedSet, SortedArray, Interval } from '../../../../mol-data/int';
import { Structure } from '../structure';
import { hashFnv32a, hash2 } from '../../../../mol-data/util';
import { SortedRanges } from '../../../../mol-data/int/sorted-ranges';
import { Loci } from './loci';
import { MolScriptBuilder as MS } from '../../../../mol-script/language/builder';
import { StructureSelection } from '../../query';
export var Bundle;

(function (Bundle) {
  Bundle.Empty = {
    hash: -1,
    elements: []
  };

  function fromSubStructure(parent, structure) {
    return fromLoci(StructureSelection.toLociWithSourceUnits(StructureSelection.Singletons(parent, structure)));
  }

  Bundle.fromSubStructure = fromSubStructure;

  function fromSelection(selection) {
    return fromLoci(StructureSelection.toLociWithSourceUnits(selection));
  }

  Bundle.fromSelection = fromSelection;

  function fromLoci(loci) {
    var _elements = [];

    for (var _i = 0, _a = loci.elements; _i < _a.length; _i++) {
      var e = _a[_i];
      var unit = e.unit,
          indices = e.indices;
      if (OrderedSet.size(indices) === 0) continue;
      var ranges = [];
      var set = [];

      if (OrderedSet.isInterval(indices)) {
        if (OrderedSet.size(indices) === 1) {
          set.push(Interval.min(indices));
        } else {
          ranges.push(Interval.min(indices), Interval.max(indices));
        }
      } else {
        var i = 0,
            len = indices.length;

        while (i < len) {
          var start = i;
          i++;

          while (i < len && indices[i - 1] + 1 === indices[i]) i++;

          var end = i;

          if (end - start > 2) {
            ranges.push(indices[start], indices[end - 1]);
          } else {
            for (var j = start; j < end; j++) {
              set[set.length] = indices[j];
            }
          }
        }
      }

      _elements.push({
        unit: unit,
        set: SortedArray.ofSortedArray(set),
        ranges: SortedRanges.ofSortedRanges(ranges)
      });
    }

    var elementGroups = new Map();

    for (var i = 0, il = _elements.length; i < il; ++i) {
      var e = _elements[i];
      var key = hash2(hashFnv32a(e.ranges), hashFnv32a(e.set));

      if (elementGroups.has(key)) {
        var groupedUnits = elementGroups.get(key).groupedUnits;

        if (groupedUnits.has(e.unit.invariantId)) {
          groupedUnits.get(e.unit.invariantId).push(e.unit.id);
        } else {
          groupedUnits.set(e.unit.invariantId, [e.unit.id]);
        }
      } else {
        var groupedUnits = new Map();
        groupedUnits.set(e.unit.invariantId, [e.unit.id]);
        elementGroups.set(key, {
          groupedUnits: groupedUnits,
          set: e.set,
          ranges: e.ranges
        });
      }
    }

    var elements = [];
    elementGroups.forEach(function (e) {
      var groupedUnits = [];
      e.groupedUnits.forEach(function (g) {
        return groupedUnits.push(SortedArray.ofUnsortedArray(g));
      });
      groupedUnits.sort(function (a, b) {
        return a[0] - b[0];
      }); // sort by first unit id of each group

      elements.push({
        groupedUnits: groupedUnits,
        set: e.set,
        ranges: e.ranges
      });
    });
    return {
      hash: loci.structure.hashCode,
      elements: elements
    };
  }

  Bundle.fromLoci = fromLoci;

  function getUnitsFromIds(unitIds, structure) {
    var units = [];

    for (var i = 0, il = unitIds.length; i < il; ++i) {
      var unitId = unitIds[i];
      if (structure.unitMap.has(unitId)) units.push(structure.unitMap.get(unitId));
    }

    return units;
  }

  function toLoci(bundle, structure) {
    if (bundle.hash !== -1 && bundle.hash !== structure.hashCode) {
      new Error('Bundle not compatible with given structure');
    }

    var elements = [];

    for (var _i = 0, _a = bundle.elements; _i < _a.length; _i++) {
      var e = _a[_i];

      var _loop_1 = function (g) {
        var units = getUnitsFromIds(g, structure);
        if (units.length === 0) return "continue";
        var indices = void 0;

        if (e.ranges.length === 0) {
          indices = e.set;
        } else if (e.set.length === 0) {
          if (e.ranges.length === 2) {
            indices = Interval.ofRange(e.ranges[0], e.ranges[1]);
          } else {
            var _indices_1 = new Int32Array(SortedRanges.size(e.ranges));

            SortedRanges.forEach(e.ranges, function (v, i) {
              return _indices_1[i] = v;
            });
            indices = SortedArray.ofSortedArray(_indices_1);
          }
        } else {
          var rangesSize = SortedRanges.size(e.ranges);

          var _indices_2 = new Int32Array(e.set.length + rangesSize);

          SortedRanges.forEach(e.ranges, function (v, i) {
            return _indices_2[i] = v;
          });

          _indices_2.set(e.set, rangesSize);

          indices = SortedArray.ofUnsortedArray(_indices_2); // requires sort
        }

        for (var _d = 0, units_1 = units; _d < units_1.length; _d++) {
          var unit = units_1[_d];
          elements.push({
            unit: unit,
            indices: indices
          });
        }
      };

      for (var _b = 0, _c = e.groupedUnits; _b < _c.length; _b++) {
        var g = _c[_b];

        _loop_1(g);
      }
    }

    return Loci(structure, elements);
  }

  Bundle.toLoci = toLoci;

  function toStructure(bundle, parent) {
    if (bundle.hash !== -1 && bundle.hash !== parent.hashCode) {
      new Error('Bundle not compatible with given structure');
    }

    var units = [];

    for (var _i = 0, _a = bundle.elements; _i < _a.length; _i++) {
      var e = _a[_i];

      var _loop_2 = function (g) {
        var _units = getUnitsFromIds(g, parent);

        if (_units.length === 0) return "continue";
        var ue = _units[0].elements; // the elements are grouped by unit.invariantId

        var rangesSize = SortedRanges.size(e.ranges);
        var setSize = e.set.length;

        var _indices = new Int32Array(setSize + rangesSize);

        var indices = void 0;

        if (rangesSize === 0) {
          for (var i = 0, il = setSize; i < il; ++i) {
            _indices[i] = ue[e.set[i]];
          }

          indices = SortedArray.ofSortedArray(_indices);
        } else if (setSize === 0) {
          SortedRanges.forEach(e.ranges, function (v, i) {
            return _indices[i] = ue[v];
          });
          indices = SortedArray.ofSortedArray(_indices);
        } else {
          if (SortedArray.min(e.set) > SortedRanges.max(e.ranges)) {
            SortedRanges.forEach(e.ranges, function (v, i) {
              return _indices[i] = ue[v];
            });

            for (var i = 0, il = setSize; i < il; ++i) {
              _indices[i + rangesSize] = ue[e.set[i]];
            }

            indices = SortedArray.ofSortedArray(_indices);
          } else if (SortedRanges.min(e.ranges) > SortedArray.max(e.set)) {
            for (var i = 0, il = setSize; i < il; ++i) {
              _indices[i] = ue[e.set[i]];
            }

            SortedRanges.forEach(e.ranges, function (v, i) {
              return _indices[i + setSize] = ue[v];
            });
            indices = SortedArray.ofSortedArray(_indices);
          } else {
            SortedRanges.forEach(e.ranges, function (v, i) {
              return _indices[i] = ue[v];
            });

            for (var i = 0, il = setSize; i < il; ++i) {
              _indices[i + rangesSize] = ue[e.set[i]];
            }

            indices = SortedArray.ofUnsortedArray(_indices); // requires sort
          }
        }

        for (var _d = 0, _units_1 = _units; _d < _units_1.length; _d++) {
          var unit = _units_1[_d];
          units.push(unit.getChild(indices));
        }
      };

      for (var _b = 0, _c = e.groupedUnits; _b < _c.length; _b++) {
        var g = _c[_b];

        _loop_2(g);
      }
    }

    return Structure.create(units, {
      parent: parent
    });
  }

  Bundle.toStructure = toStructure;

  function elementToExpression(e) {
    return MS.internal.generator.bundleElement({
      groupedUnits: MS.core.type.list(e.groupedUnits.map(function (u) {
        return MS.core.type.list(u);
      })),
      ranges: MS.core.type.list(e.ranges),
      set: MS.core.type.list(e.set)
    });
  }

  function toExpression(bundle) {
    return MS.internal.generator.bundle({
      elements: MS.core.type.list(bundle.elements.map(elementToExpression))
    });
  }

  Bundle.toExpression = toExpression;

  function areEqual(a, b) {
    if (a.elements.length !== b.elements.length) return false;

    for (var i = 0, il = a.elements.length; i < il; ++i) {
      var elementA = a.elements[i],
          elementB = b.elements[i];
      if (elementA.groupedUnits.length !== elementB.groupedUnits.length) return false;

      for (var j = 0, jl = elementB.groupedUnits.length; j < jl; ++j) {
        if (!SortedArray.areEqual(elementA.groupedUnits[j], elementB.groupedUnits[j])) return false;
      }

      if (!SortedArray.areEqual(elementA.set, elementB.set)) return false;
      if (!SortedRanges.areEqual(elementA.ranges, elementB.ranges)) return false;
    }

    return true;
  }

  Bundle.areEqual = areEqual;
})(Bundle || (Bundle = {}));